import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import { create } from 'zustand';
import { createDocument, type Item, type Page, type WorksheetDocument } from '@abgen/model';

enablePatches();

/** Mindestens 100 Schritte sind gefordert (Phase 1.5); wir halten mehr. */
export const HISTORY_LIMIT = 200;

export interface HistoryEntry {
  label: string;
  patches: Patch[];
  inverse: Patch[];
  /** Zusammenfassung mehrerer Änderungen zu einem Schritt (ein Ziehvorgang). */
  key?: string;
}

export interface EditorState {
  document: WorksheetDocument;
  past: HistoryEntry[];
  future: HistoryEntry[];
  /** Ungespeicherte Änderungen vorhanden. */
  dirty: boolean;
  /** Serverseitige Dokument-ID, sobald gespeichert (Phase 1.7). */
  remoteId: string | null;
  apply(
    label: string,
    recipe: (draft: WorksheetDocument) => void,
    options?: { coalesceKey?: string },
  ): void;
  undo(): void;
  redo(): void;
  canUndo(): boolean;
  canRedo(): boolean;
  /** Dokument ersetzen (Laden, Neu); löscht die Historie. */
  load(document: WorksheetDocument, remoteId?: string | null): void;
  markSaved(remoteId?: string | null): void;
  /** Letzten Schritt benennen – für die Anzeige „Rückgängig: …“. */
  undoLabel(): string | null;
  redoLabel(): string | null;
}

export const useEditorStore = create<EditorState>((set, get) => ({
  document: createDocument(),
  past: [],
  future: [],
  dirty: false,
  remoteId: null,

  apply: (label, recipe, options) => {
    const state = get();
    const [changed, patches, inverse] = produceWithPatches(state.document, recipe);
    if (patches.length === 0) return;
    // Zeitstempel erst setzen, wenn die Änderung tatsächlich etwas bewirkt hat.
    const [next, stampPatches, stampInverse] = produceWithPatches(changed, (draft) => {
      draft.meta.updatedAt = new Date().toISOString();
    });
    patches.push(...stampPatches);
    inverse.unshift(...stampInverse);

    const past = [...state.past];
    const last = past[past.length - 1];
    if (options?.coalesceKey && last && last.key === options.coalesceKey) {
      past[past.length - 1] = {
        ...last,
        patches: [...last.patches, ...patches],
        inverse: [...inverse, ...last.inverse],
      };
    } else {
      past.push({
        label,
        patches,
        inverse,
        ...(options?.coalesceKey ? { key: options.coalesceKey } : {}),
      });
      if (past.length > HISTORY_LIMIT) past.shift();
    }

    set({ document: next, past, future: [], dirty: true });
  },

  undo: () => {
    const { past, future, document } = get();
    const entry = past[past.length - 1];
    if (!entry) return;
    set({
      document: applyPatches(document, entry.inverse),
      past: past.slice(0, -1),
      future: [entry, ...future],
      dirty: true,
    });
  },

  redo: () => {
    const { past, future, document } = get();
    const entry = future[0];
    if (!entry) return;
    set({
      document: applyPatches(document, entry.patches),
      past: [...past, entry],
      future: future.slice(1),
      dirty: true,
    });
  },

  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,
  undoLabel: () => get().past[get().past.length - 1]?.label ?? null,
  redoLabel: () => get().future[0]?.label ?? null,

  load: (document, remoteId = null) =>
    set({ document, past: [], future: [], dirty: false, remoteId }),

  markSaved: (remoteId) => set((state) => ({ dirty: false, remoteId: remoteId ?? state.remoteId })),
}));

// ---------------------------------------------------------------------------
// Zugriffshilfen
// ---------------------------------------------------------------------------

export function currentPage(document: WorksheetDocument, pageIndex: number): Page | undefined {
  return document.pages[pageIndex];
}

export function findItem(
  document: WorksheetDocument,
  id: string,
): { item: Item; pageIndex: number; index: number } | undefined {
  for (const [pageIndex, page] of document.pages.entries()) {
    const index = page.items.findIndex((item) => item.id === id);
    if (index >= 0) return { item: page.items[index]!, pageIndex, index };
  }
  return undefined;
}

export function itemsOnPage(document: WorksheetDocument, pageIndex: number): Item[] {
  return document.pages[pageIndex]?.items ?? [];
}

/** Felder einer Auswahl, in Ebenenreihenfolge der Seite. */
export function selectedItems(
  document: WorksheetDocument,
  pageIndex: number,
  ids: readonly string[],
): Item[] {
  const set = new Set(ids);
  return itemsOnPage(document, pageIndex).filter((item) => set.has(item.id));
}
